/* eslint-disable max-len */
import React, { useState, useEffect, useMemo } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Footer from '~/pages/Manager/Footer';
import PF2MTable from '~/components/PF2MTable';
import {
  setContentVariableValue,
  resetContentVariables,
  getContents,
  importContentVariables,
  exportContentVariables,
  getContentVariablesTemplate,
} from '~/store/quality/actions';
import {
  getElements,
  getSubElements,
  getMaterials,
  resetFileUploadErrors,
} from '~/store/manager/actions';
import Header, { analysisTypes } from './Header';
import ImportExportHeader from '~/pages/Quality/Header';
import styles from '~/pages/Quality/styles';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import { newMomentDate, parseDate } from '~/utils/moment';
import PF2MDatepickerNew from '~/components/PF2MDatepickerNew';
import PF2MSearchButton from '~/components/PF2MSearchButton';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import usePlatformConfiguration from '~/services/platformConfiguration';
import { getContentVariablesV2, updateContentVariablesV2 } from '~/store/quality/actions_v2';

const buildSearchParams = (start, end) => ({
  startTimestamp: start.unix(),
  endTimestamp: end.unix(),
});

const defaultStartDate = () => newMomentDate().startOf('day');
const defaultEndDate = () => newMomentDate();

const ContentVariablesTab = ({ classes }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const showAnalysisType = usePlatformConfiguration('grade_control_show_analysis_type');

  const [showAlert, setShowAlert] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [columnCreated, setColumnCreated] = useState(false);
  const [startDatetime, setStartDatetime] = useState(defaultStartDate);
  const [endDatetime, setEndDatetime] = useState(defaultEndDate);
  const [searchParams, setSearchParams] = useState(() => buildSearchParams(defaultStartDate(), defaultEndDate()));
  const [element, setElement] = useState(0);
  const [subElement, setSubElement] = useState(0);
  const [material, setMaterial] = useState(0);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [columns, setColumns] = useState([]);

  const elements = useSelector(state => state.manager.elements.filter(r => r.is_origin));
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.manager.materials);
  const contents = useSelector(state => state.quality.contents);
  const contentVariables = useSelector(state => state.quality.contentVariables);
  const contentVariablesFlat = useSelector(state => state.quality.contentVariablesFlat);
  const isContentVariablesDirty = useSelector(state => state.quality.isContentVariablesDirty);
  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);

  useEffect(() => {
    async function fetchMetadata() {
      await Promise.all([
        dispatch(getContents()),
        dispatch(getElements()),
        dispatch(getSubElements()),
        dispatch(getMaterials()),
      ]);
    }
    if (!dataLoaded) {
      fetchMetadata();
      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  useEffect(() => {
    dispatch(getContentVariablesV2(searchParams));
  }, [searchParams, dispatch]);

  const handleSearch = () => {
    setSearchParams(buildSearchParams(startDatetime, endDatetime));
  };

  useEffect(() => {
    if (!columnCreated
      && contents.length > 0
      && elements.length > 0
      && subElements.length > 0
      && materials.length > 0) {
      const newColumns = contents
        .filter(c => c.active)
        .map(c => ({
          field: `content_${c.id}_${Number(!!c.value_type)}`,
          title: `${c.name}`,
          editable: true,
          type: c.value_type === 1 ? 'text' : 'decimal',
          decimalPrecision: 5,
          width: '100px',
        }));
      const initialColumns = [
        {
          field: 'start_timestamp',
          timeField: 'start_time',
          dateField: 'start_date',
          title: translate('common:StartDate'),
          editable: true,
          updateOnChange: true,
          width: '150px',
          dateTimePicker: true,
          disableFuture: false,
        },
        {
          field: 'end_timestamp',
          timeField: 'end_time',
          dateField: 'end_date',
          title: translate('common:EndDate'),
          editable: true,
          updateOnChange: true,
          width: '150px',
          dateTimePicker: true,
          disableFuture: false,
        },
        {
          field: 'element_id',
          title: translate('common:Element'),
          editable: true,
          selectItems: elements,
          width: '160px',
          checkDuplicates: true,
        },
        {
          field: 'element_point_id',
          title: translate('common:SubElement'),
          editable: true,
          selectItems: subElements,
          filterKey: 'id_element',
          filterValue: 'element_id',
          width: '160px',
        },
        {
          field: 'material_id',
          title: translate('common:Material'),
          editable: true,
          selectItems: materials,
          width: '160px',
        },
        showAnalysisType.value === '1' && {
          field: 'analysis_type_id',
          title: translate('common:AnalysisType'),
          editable: true,
          width: '160px',
          selectItems: analysisTypes(translate),
        },
      ];
      setColumns([...initialColumns, ...newColumns]);
      setColumnCreated(true);
    }
  }, [columnCreated, contents, elements, subElements, materials, translate]);

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
    }
  }, [fileUploadErrors]);

  const renderDateTimePicker = (value, setAction) => (
    <PF2MDatepickerNew
      isFormControlled={false}
      value={value}
      onChange={setAction}
      isDateTime
      views={['year', 'month', 'date', 'hours', 'minutes', 'seconds']}
      format={translate('date:FullDateTimeFormat')}
      disableFuture={false}
      InputProps={{
        style: {
          minWidth: 150,
          height: 40,
        },
      }}
      style={{
        width: '100%',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        height: 38,
        paddingLeft: 4,
      }}
    />
  );

  const renderItems = (items = []) => [{ value: 0, label: translate('common:Select') }]
    .concat(...items
      .map(e => ({ value: e.id, label: e.name })));

  const renderFiltersTable = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 200px)',
      gridColumnGap: 10,
      gridRowGap: 10,
      margin: '10px 0',
    }}
    >
      {/* Doc: StartDatetime and EndDatetime filters only for server side filtering */}
      <FormControl>
        <FormLabel className={classes.formLabel}>
          {translate('common:StartDate')}
        </FormLabel>
        <FormGroup>
          {renderDateTimePicker(startDatetime, setStartDatetime)}
        </FormGroup>
      </FormControl>
      <FormControl>
        <FormLabel className={classes.formLabel}>
          {translate('common:EndDate')}
        </FormLabel>
        <FormGroup>
          {renderDateTimePicker(endDatetime, setEndDatetime)}
        </FormGroup>
      </FormControl>
      {/* Doc: Element, SubElement and Material filters only for client side filtering */}
      <FormControl>
        <FormLabel className={classes.formLabel}>
          {translate('common:Element')}
        </FormLabel>
        <FormGroup>
          <PF2MSearchSelectOutlined
            value={element}
            onChange={e => setElement(e.target.value)}
            className={classes.fieldSelect}
          >
            {renderItems(elements)}
          </PF2MSearchSelectOutlined>
        </FormGroup>
      </FormControl>
      <FormControl>
        <FormLabel className={classes.formLabel}>
          {translate('common:SubElement')}
        </FormLabel>
        <FormGroup>
          <PF2MSearchSelectOutlined
            value={subElement}
            onChange={e => setSubElement(e.target.value)}
            className={classes.fieldSelect}
          >
            {renderItems(subElements.filter(s => s.id_element === element))}
          </PF2MSearchSelectOutlined>
        </FormGroup>
      </FormControl>
      <FormControl>
        <FormLabel className={classes.formLabel}>
          {translate('common:Material')}
        </FormLabel>
        <FormGroup>
          <PF2MSearchSelectOutlined
            value={material}
            onChange={e => setMaterial(e.target.value)}
            className={classes.fieldSelect}
          >
            {renderItems(materials)}
          </PF2MSearchSelectOutlined>
        </FormGroup>
      </FormControl>
      <FormControl style={{ width: 200 }}>
        <FormLabel className={classes.formLabel} style={{ visibility: 'hidden' }}>
          {translate('common:Search')}
        </FormLabel>
        <FormGroup>
          <PF2MSearchButton
            onClick={handleSearch}
            size="small"
            style={{
              width: '100%',
              height: 38,
              minHeight: 38,
              margin: 0,
              boxShadow: 'none',
            }}
          />
        </FormGroup>
      </FormControl>
    </div>
  );

  const displayContent = useMemo(() => contentVariablesFlat.filter(
    c => (element === 0 || c.element_id === element)
      && (subElement === 0 || c.element_point_id === subElement)
      && (material === 0 || c.material_id === material),
  ), [contentVariablesFlat, element, subElement, material]);

  const [checkDuplicateIds, hasDuplicates] = useMemo(() => {
    const keyMap = new Map();
    const duplicates = new Set();

    displayContent.forEach((item) => {
      const isPartial = item.analysis_type_id === 2;
      if (isPartial) return;

      const key = `${item.element_id}-${item.element_point_id}-${item.material_id}`;
      const startTs = parseDate(item.start_date, true).valueOf();
      const endTs = parseDate(item.end_date, true).valueOf();

      if (keyMap.has(key)) {
        keyMap.get(key).forEach((existing) => {
          if (startTs <= existing.endTs && endTs >= existing.startTs) {
            duplicates.add(item.id);
            duplicates.add(existing.id);
          }
        });
        keyMap.get(key).push({ startTs, endTs, id: item.id });
      } else {
        keyMap.set(key, [{ startTs, endTs, id: item.id }]);
      }
    });

    return [Array.from(duplicates), duplicates.size > 0];
  }, [displayContent]);

  return (
    <div className={classes.tabContainer}>
      <ImportExportHeader
        getTemplateAction={() => dispatch(getContentVariablesTemplate())}
        importAction={file => dispatch(importContentVariables(file))}
        exportAction={() => dispatch(exportContentVariables())}
      />
      <Header />
      <div style={{
        border: '1px solid #CDCDCD',
        borderRadius: 5,
        padding: 10,
        margin: '10px 0',
      }}
      >
        <span className={classes.labelTypeSelector}>
          {translate('common:AssociationsList')}
        </span>
        {/* {renderFiltersDate()} */}
        {renderFiltersTable()}
        <PF2MTable
          data={displayContent}
          columns={columns}
          duplicateIds={checkDuplicateIds}
          updateItem={(row, field, value) => {
            dispatch(setContentVariableValue(row, field, value));
          }}
          className={classes.table}
        />
      </div>
      <Footer
        isDirty={isContentVariablesDirty && !hasDuplicates}
        discard={() => dispatch(resetContentVariables())}
        sendData={() => dispatch(updateContentVariablesV2(
          contentVariablesFlat,
          contentVariables,
          searchParams,
        ))}
      />
      <PF2MAlertDialog
        confirmText={translate('common:Understood')}
        description={translate('validation:WarningScheduleChange')}
        open={showAlert}
        onClose={() => setShowAlert(false)}
        onConfirm={() => {
          setShowAlert(false);
          dispatch(updateContentVariablesV2(
            contentVariables,
            contentVariables,
            searchParams,
          ));
        }}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
    </div>
  );
};

ContentVariablesTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

ContentVariablesTab.defaultProps = {};

export default withStyles(styles)(ContentVariablesTab);
